import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { api, errMsg, STATUS_LABEL } from "../lib/api";

const Person = ({ u }) =>
  u ? (
    <div>
      <p className="font-semibold">{u.name}</p>
      <p className="text-xs text-stone-400">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
    </div>
  ) : <span className="text-stone-600">—</span>;

const fmt = (d) => (d ? new Date(d).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function AdminPage() {
  const [spots, setSpots] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/admin/spots").then((r) => setSpots(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa segnalazione?")) return;
    try {
      await api.delete(`/admin/spots/${id}`);
      toast.success("Segnalazione eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const filtered = users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const head = "text-stone-400";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 rise">
      <p className="font-mono-x text-[11px] uppercase tracking-wider text-amber-400">Area riservata</p>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Pannello Admin</h2>
      <p className="text-sm text-stone-400 mt-1">Qui vedi tutte le informazioni, incluse le identità di chi lascia e di chi prenota.</p>

      <Tabs defaultValue="spots" className="mt-6">
        <TabsList className="bg-stone-900">
          <TabsTrigger value="spots" data-testid="admin-tab-spots">Segnalazioni ({spots.length})</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Utenti ({users.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="spots">
          <div className="rounded-2xl border border-stone-800 overflow-x-auto">
            <Table data-testid="admin-spots-table">
              <TableHeader>
                <TableRow className="border-stone-800">
                  <TableHead className={head}>Indirizzo</TableHead>
                  <TableHead className={head}>Chi lascia</TableHead>
                  <TableHead className={head}>Chi prenota</TableHead>
                  <TableHead className={head}>Partenza</TableHead>
                  <TableHead className={head}>Stato</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {spots.map((s) => (
                  <TableRow key={s.id} data-testid={`admin-spot-row-${s.id}`} className="border-stone-800">
                    <TableCell><p className="font-semibold">{s.address}</p><p className="text-xs text-stone-500">{[s.vehicle, s.note].filter(Boolean).join(" · ")}</p></TableCell>
                    <TableCell><Person u={s.leaver} /></TableCell>
                    <TableCell><Person u={s.reserver} /></TableCell>
                    <TableCell className="text-sm">{fmt(s.leave_at)}</TableCell>
                    <TableCell className="text-sm">{STATUS_LABEL[s.status]}</TableCell>
                    <TableCell>
                      <button data-testid={`admin-delete-spot-${s.id}`} onClick={() => del(s.id)} className="h-9 w-9 grid place-items-center rounded-full text-stone-400 hover:text-red-400 hover:bg-stone-800 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {spots.length === 0 && <TableRow><TableCell colSpan={6} className="text-stone-500">Nessuna segnalazione</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-3">
          <Input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o email" className="max-w-sm h-11 bg-stone-900 border-stone-700" />
          <div className="rounded-2xl border border-stone-800 overflow-x-auto">
            <Table data-testid="admin-users-table">
              <TableHeader>
                <TableRow className="border-stone-800">
                  <TableHead className={head}>Utente</TableHead>
                  <TableHead className={head}>Ruolo</TableHead>
                  <TableHead className={head}>Posti lasciati</TableHead>
                  <TableHead className={head}>Prenotazioni</TableHead>
                  <TableHead className={head}>Iscritto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow key={u.id} className="border-stone-800">
                    <TableCell><Person u={u} /></TableCell>
                    <TableCell className="text-sm">{u.role === "admin" ? "Admin" : "Utente"}</TableCell>
                    <TableCell className="font-mono-x">{u.spots_count}</TableCell>
                    <TableCell className="font-mono-x">{u.reservations_count}</TableCell>
                    <TableCell className="text-sm">{fmt(u.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
